import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api, { workTasksAPI, staffAPI, clientAPI } from '../services/api';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { INBOX_TYPE_OPTIONS } from '../constants/inboxTypes';
import ContentHeader from '../components/ContentHeader';
import './TestTools.css';

/**
 * Test / utility tools for demos and development.
 * Visible only to managers and super admin (see App.js Settings dropdown).
 * Create test inbox items: until real flows (e.g. "client assigned to coordinator") create inbox items automatically.
 */
function TestTools() {
  const { tenantPath } = useTenantFromUrl();
  const [roleCode, setRoleCode] = useState(null);
  const [myStaffId, setMyStaffId] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [clientList, setClientList] = useState([]);
  const [createAssignee, setCreateAssignee] = useState('');
  const [createType, setCreateType] = useState('assignment');
  const [createDescription, setCreateDescription] = useState('');
  const [createDetails, setCreateDetails] = useState('');
  const [createDueAt, setCreateDueAt] = useState('');
  const [createClientId, setCreateClientId] = useState('');
  const [createError, setCreateError] = useState('');
  const [createSuccess, setCreateSuccess] = useState('');
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/whoami');
        setRoleCode(res.data?.membership?.role_code ?? null);
        const sid = res.data?.user?.staff_id ?? null;
        setMyStaffId(sid != null ? String(sid) : null);
        setIsSuperAdmin(res.data?.is_super_admin === true);
      } catch {
        setRoleCode(null);
        setMyStaffId(null);
        setIsSuperAdmin(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  useEffect(() => {
    if (loading) return;
    (async () => {
      try {
        const [staffRes, clientRes] = await Promise.all([
          staffAPI.getAll({ active: 'true' }),
          clientAPI.getAll({ page: 1, pageSize: 100 })
        ]);
        setStaffList(staffRes.data?.data ?? []);
        setClientList(clientRes.data?.data ?? []);
      } catch {
        setStaffList([]);
        setClientList([]);
      }
    })();
  }, [loading]);

  const assigneeOptions =
    roleCode === 'SERVICE_COORDINATOR'
      ? myStaffId
        ? [{ id: myStaffId, first_name: 'Me', last_name: '(you)' }]
        : []
      : staffList;

  const canUseTestTools = isSuperAdmin || (roleCode && roleCode !== 'SERVICE_COORDINATOR');

  const handleCreateAssignment = async (e) => {
    e.preventDefault();
    setCreateError('');
    setCreateSuccess('');
    if (!createAssignee || !createDescription.trim()) {
      setCreateError('Select assignee and enter a description.');
      return;
    }
    setCreateSubmitting(true);
    try {
      await workTasksAPI.createInboxItem({
        assignee_id: parseInt(createAssignee, 10),
        type: createType || undefined,
        description: createDescription.trim(),
        details: createDetails.trim() || undefined,
        due_at: createDueAt.trim() || undefined,
        client_id: createClientId.trim() || undefined
      });
      setCreateSuccess('Inbox item created. It will appear on the Work Tasks dashboard for the assignee.');
      setCreateDescription('');
      setCreateDetails('');
      setCreateDueAt('');
      setCreateClientId('');
    } catch (err) {
      setCreateError(err.response?.data?.error || 'Failed to create inbox item');
    } finally {
      setCreateSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="test-tools-page">
        <p>Loading…</p>
      </div>
    );
  }

  if (!canUseTestTools) {
    return (
      <div className="test-tools-page">
        <ContentHeader
          title="Test tools"
          backTo={tenantPath('/dashboard')}
          backLabel="Back to Work Tasks"
          titleHint="This page is for managers and administrators. Use it to create test inbox items for demos until real workflows are wired."
        />
      </div>
    );
  }

  return (
    <div className="test-tools-page">
      <ContentHeader
        title="Test tools"
        titleHint="Utility for demos and testing. Create a test inbox item (client assignment, file review, AT packet, or generic assignment) so it appears on the Work Tasks dashboard. Once real flows create these automatically, this can be removed or hidden."
      />

      <section className="test-tools-section">
        <h2>Create test inbox item</h2>
        <form className="create-assignment-form" onSubmit={handleCreateAssignment}>
          <div className="form-row">
            <label htmlFor="create-type">Type</label>
            <select
              id="create-type"
              value={createType}
              onChange={(e) => setCreateType(e.target.value)}
            >
              {INBOX_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="create-assignee">Assign to</label>
            <select
              id="create-assignee"
              value={createAssignee}
              onChange={(e) => setCreateAssignee(e.target.value)}
              required
            >
              <option value="">Select…</option>
              {assigneeOptions.map((s) => (
                <option key={s.id} value={String(s.id)}>
                  {s.first_name} {s.last_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-row">
            <label htmlFor="create-description">Description (subject)</label>
            <textarea
              id="create-description"
              value={createDescription}
              onChange={(e) => setCreateDescription(e.target.value)}
              placeholder="e.g. New client assigned: Jane Doe, DOB 2020-01-15"
              rows={2}
              required
            />
          </div>
          <div className="form-row">
            <label htmlFor="create-details">Details (optional)</label>
            <textarea
              id="create-details"
              value={createDetails}
              onChange={(e) => setCreateDetails(e.target.value)}
              placeholder="Additional message; shown when the assignee expands the inbox row"
              rows={3}
            />
          </div>
          <div className="form-row">
            <label htmlFor="create-due">Due date (optional)</label>
            <input
              id="create-due"
              type="date"
              value={createDueAt}
              onChange={(e) => setCreateDueAt(e.target.value)}
            />
          </div>
          <div className="form-row">
            <label htmlFor="create-client">Link to client (optional)</label>
            <select
              id="create-client"
              value={createClientId}
              onChange={(e) => setCreateClientId(e.target.value)}
            >
              <option value="">None</option>
              {clientList.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.last_name}, {c.first_name}
                </option>
              ))}
            </select>
            <small className="form-hint">If set, assignee will see a &quot;View client&quot; button.</small>
          </div>
          {createError && <p className="create-assignment-error">{createError}</p>}
          {createSuccess && <p className="create-assignment-success">{createSuccess}</p>}
          <div className="form-actions">
            <button type="submit" className="btn-submit-assignment" disabled={createSubmitting}>
              {createSubmitting ? 'Creating…' : 'Create inbox item'}
            </button>
          </div>
        </form>
        <p className="test-tools-after-form">
          <Link to={tenantPath('/dashboard')}>Open Dashboard</Link> to see the new item (filter by assignee if needed).
        </p>
      </section>
    </div>
  );
}

export default TestTools;
